import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  connectChannel,
  disconnectChannel,
  fetchIntegrationsOverview,
  removeProviderKey,
  setProviderKey,
  testChannel,
  testProviderConnection,
} from '@/api/integrations';
import { queryKeys } from '@/lib/query-keys';
import type { ChannelCredentials, ChannelName, ProviderName } from '@/types/integrations';

/**
 * Integraciones del negocio activo (módulo 2).
 *
 * El resumen (`GET .../integrations`) lo puede leer cualquier miembro; guardar/borrar/probar
 * es solo ADMIN. Las API keys NUNCA se devuelven en claro: el estado que se pinta es
 * `{ configurada, preview }` (una máscara tipo `••••1234`).
 *
 * Toda mutación invalida la key `['tenants', tenantId, 'integrations']`, que es padre de
 * `integrations.channels` y de las sesiones OAuth pendientes: un solo `invalidateQueries`
 * mantiene la pantalla coherente.
 */

/** `GET /api/tenants/:tenantId/integrations` (estado de las API keys y de los canales). */
export function useIntegrationsOverview(tenantId: string | null) {
  return useQuery({
    queryKey: queryKeys.integrations.overview(tenantId ?? ''),
    queryFn: () => fetchIntegrationsOverview(tenantId ?? ''),
    enabled: Boolean(tenantId),
  });
}

function invalidateIntegrations(queryClient: QueryClient, tenantId: string | null): void {
  if (!tenantId) return;
  queryClient.invalidateQueries({ queryKey: queryKeys.integrations.overview(tenantId) });
}

/** `PUT /api/tenants/:tenantId/integrations/:provider` (solo ADMIN). */
export function useSetProviderKey(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ provider, apiKey }: { provider: ProviderName; apiKey: string }) =>
      setProviderKey(tenantId ?? '', provider, apiKey),
    onSuccess: () => invalidateIntegrations(queryClient, tenantId),
  });
}

/** `DELETE /api/tenants/:tenantId/integrations/:provider` (solo ADMIN). */
export function useRemoveProviderKey(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (provider: ProviderName) => removeProviderKey(tenantId ?? '', provider),
    onSuccess: () => invalidateIntegrations(queryClient, tenantId),
  });
}

/**
 * `POST .../integrations/:provider/test` (solo ADMIN).
 * Sin `apiKey` prueba la guardada; con `apiKey` prueba la que el usuario acaba de escribir.
 * El backend responde 200 con `{ ok, message }`: el veredicto va en `ok`, no en el status.
 */
export function useTestProvider(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ provider, apiKey }: { provider: ProviderName; apiKey?: string }) =>
      testProviderConnection(tenantId ?? '', provider, apiKey),
    onSuccess: () => invalidateIntegrations(queryClient, tenantId),
  });
}

/** `PUT .../integrations/channels/:channel` (solo ADMIN; valida y cifra las credenciales). */
export function useConnectChannel(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ channel, credenciales }: { channel: ChannelName; credenciales: ChannelCredentials }) =>
      connectChannel(tenantId ?? '', channel, credenciales),
    onSuccess: () => invalidateIntegrations(queryClient, tenantId),
  });
}

/** `DELETE .../integrations/channels/:channel` (solo ADMIN). */
export function useDisconnectChannel(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channel: ChannelName) => disconnectChannel(tenantId ?? '', channel),
    onSuccess: () => invalidateIntegrations(queryClient, tenantId),
  });
}

/**
 * `POST .../integrations/channels/:channel/test` (solo ADMIN).
 * Prueba contra la Graph API de Meta con las credenciales guardadas (o las del body).
 */
export function useTestChannel(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ channel, credenciales }: { channel: ChannelName; credenciales?: ChannelCredentials }) =>
      testChannel(tenantId ?? '', channel, credenciales),
    onSuccess: () => invalidateIntegrations(queryClient, tenantId),
  });
}
